/**
 * Accessible REST form submission for public dt-form blocks.
 *
 * @package DemocraticTools
 */

import { clearPendingSubscriberHeader, rememberSubscribedBrowser } from './preferences.js';
import { readSubscribeProductSelections, withSubscribeContext, withSubscribeProductPayload } from './subscribe-form.js';

/**
 * @typedef {Object} FormConfig
 * @property {Record<string, string>} messages
 * @property {{ liveError: string, liveSuccess: string }} selectors
 * @property {{ url?: string, maxBytes?: number, maxFiles?: number }} [upload]
 */

/**
 * @typedef {Object} RestErrorPayload
 * @property {string} [error]
 * @property {string} [message]
 * @property {{ fields?: Array<{ field: string, message: string }> }} [a11y]
 */

/**
 * @param {FormConfig|undefined} config
 */
export function initForms(config) {
  const resolved = config ?? globalThis.dtFormConfig ?? {};
  const forms = globalThis.document?.querySelectorAll('.dt-form[data-dt-rest-form]') ?? [];

  for (const form of forms) {
    if (!isFormElement(form) || form.dataset.dtFormBound === '1') {
      continue;
    }

    form.dataset.dtFormBound = '1';
    form.addEventListener('submit', (event) => {
      void handleSubmit(event, form, resolved);
    });
  }
}

/**
 * @param {SubmitEvent} event
 * @param {HTMLFormElement} form
 * @param {FormConfig} config
 */
async function handleSubmit(event, form, config) {
  event.preventDefault();
  clearFormState(form);

  const submitButton = form.querySelector('button[type="submit"]');
  const button = isElement(submitButton) && submitButton.tagName === 'BUTTON' ? submitButton : null;

  if (button) {
    button.disabled = true;
    button.dataset.dtOriginalLabel = button.textContent ?? '';
    button.textContent = config.messages?.submitting ?? 'Submitting…';
  }

  try {
    const attachmentIds = await uploadAttachments(form, config);

    if (attachmentIds === null) {
      return;
    }

    if (isSubscribeForm(form) && !hasSubscribeProductSelection(form, config)) {
      return;
    }

    const payload = isSubscribeForm(form)
      ? withSubscribeProductPayload(form, withSubscribeContext(collectPayload(form)))
      : collectPayload(form);

    if (attachmentIds.length > 0) {
      payload.attachment_ids = attachmentIds;
    }

    const response = await fetch(form.action, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-WP-Nonce': form.dataset.restNonce ?? '',
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
    });

    /** @type {RestErrorPayload & Record<string, unknown>} */
    const result = await parseJson(response);

    if (!response.ok) {
      rememberKnownSubscriber(form, result);
      applyErrorState(form, result, config);
      return;
    }

    applySuccessState(form, result, config);
    dispatchSuccess(form, result, payload);
  } catch {
    applyNetworkError(form, config);
  } finally {
    if (button) {
      button.disabled = false;

      if (button.dataset.dtOriginalLabel) {
        button.textContent = button.dataset.dtOriginalLabel;
      }
    }
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {Record<string, unknown>}
 */
export function collectPayload(form) {
  /** @type {Record<string, unknown>} */
  const payload = {};
  const elements = form.querySelectorAll('input, select, textarea');

  for (const element of elements) {
    if (!isFieldControl(element)) {
      continue;
    }

    const name = element.name;

    if (!name || element.type === 'submit' || element.type === 'file') {
      continue;
    }

    if (element.type === 'checkbox') {
      payload[name] = element.checked === true || element.hasAttribute('checked');
      continue;
    }

    payload[name] = element.value;
  }

  return payload;
}

/**
 * @param {HTMLFormElement} form
 * @param {FormConfig} config
 * @returns {Promise<number[] | null>}
 */
async function uploadAttachments(form, config) {
  const uploadConfig = config.upload ?? globalThis.dtFormConfig?.upload ?? {};
  const fileInput = form.querySelector('[data-dt-contribute-upload], input[type="file"]');

  if (!isFieldControl(fileInput) || fileInput.type !== 'file') {
    return [];
  }

  const files = fileInput.files;

  if (!files || files.length === 0) {
    return [];
  }

  const uploadUrl = uploadConfig.url ?? '';
  const maxBytes = typeof uploadConfig.maxBytes === 'number' ? uploadConfig.maxBytes : 5_242_880;
  const maxFiles = typeof uploadConfig.maxFiles === 'number' ? uploadConfig.maxFiles : 3;

  if (uploadUrl === '') {
    applyFieldError(form, 'attachments[]', config.messages?.generic_error ?? 'Please review the form and try again.');
    setStatus(form, config.messages?.generic_error ?? 'Please review the form and try again.', 'error');
    return null;
  }

  if (files.length > maxFiles) {
    const message = `You can attach up to ${maxFiles} files.`;
    applyFieldError(form, 'attachments[]', message);
    setStatus(form, message, 'error');
    return null;
  }

  /** @type {number[]} */
  const attachmentIds = [];

  for (const file of files) {
    if (file.size > maxBytes) {
      const message = 'Each file must be 5 MB or smaller.';
      applyFieldError(form, 'attachments[]', message);
      setStatus(form, message, 'error');
      return null;
    }

    const body = new FormData();
    body.append('file', file);

    let response;

    try {
      response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-WP-Nonce': form.dataset.restNonce ?? '',
        },
        body,
        credentials: 'same-origin',
      });
    } catch {
      applyNetworkError(form, config);
      return null;
    }

    /** @type {RestErrorPayload & { attachment_ids?: number[] }} */
    const payload = await parseJson(response);

    if (!response.ok) {
      applyErrorState(form, payload, config);

      const attachmentError = payload.a11y?.fields?.find((field) => field.field === 'attachments');

      if (attachmentError) {
        applyFieldError(form, 'attachments[]', attachmentError.message);
      }

      return null;
    }

    const ids = Array.isArray(payload.attachment_ids) ? payload.attachment_ids : [];

    for (const id of ids) {
      const numeric = Number(id);

      if (Number.isInteger(numeric) && numeric > 0) {
        attachmentIds.push(numeric);
      }
    }
  }

  return attachmentIds;
}

/**
 * @param {Response} response
 */
async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

/**
 * After a known address confirms consent, remember this device so Manage
 * Preferences can open their saved choices. The already-subscribed notice stays.
 *
 * @param {HTMLFormElement} form
 * @param {RestErrorPayload & Record<string, unknown>} payload
 */
function rememberKnownSubscriber(form, payload) {
  if ((form.dataset.dtRestForm ?? '') !== 'subscribe') {
    return;
  }

  const preferencesUrl = typeof payload.preferences_url === 'string' ? payload.preferences_url : '';

  if (!preferencesUrl.includes('token=')) {
    return;
  }

  const emailField = form.querySelector('[name="email"]');
  const email = isFieldControl(emailField) ? emailField.value : '';
  rememberSubscribedBrowser(email, preferencesUrl);
}

/**
 * @param {HTMLFormElement} form
 * @param {RestErrorPayload} payload
 * @param {FormConfig} config
 */

function applyErrorState(form, payload, config) {
  const raw = typeof payload.message === 'string' && payload.message.trim() !== ''
    ? payload.message
    : (config.messages?.generic_error ?? 'Please review the form and try again.');
  const message = stripHtmlMessage(raw);
  setStatus(form, message, 'error');
  announce(config.selectors?.liveError ?? '#dt-form-errors', message);

  const fields = payload.a11y?.fields ?? [];

  for (const fieldError of fields) {
    applyFieldError(form, fieldError.field, stripHtmlMessage(fieldError.message));
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, unknown>} payload
 * @param {FormConfig} config
 */
function applySuccessState(form, payload, config) {
  const formType = form.dataset.dtRestForm ?? '';
  const fallback = formType === 'contribute'
    ? config.messages?.contribution_received
    : formType === 'profile-control'
      ? config.messages?.profile_request_received
      : (config.messages?.subscribe_success ?? config.messages?.subscribe_pending);
  const message = typeof payload.message === 'string'
    ? stripHtmlMessage(payload.message)
    : (fallback ?? 'Submitted successfully.');
  setStatus(form, message, 'success');
  announce(config.selectors?.liveSuccess ?? '#dt-form-success', message);

  if (formType === 'subscribe') {
    const emailField = form.querySelector('[name="email"]');
    const email = isFieldControl(emailField) ? emailField.value : '';
    const preferencesUrl = typeof payload.preferences_url === 'string' ? payload.preferences_url : '';

    if (email.includes('@') && preferencesUrl.includes('token=')) {
      rememberSubscribedBrowser(email, preferencesUrl);
    } else {
      clearPendingSubscriberHeader();
    }
  }

  if (typeof form.reset === 'function') {
    form.reset();
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {FormConfig} config
 */
function applyNetworkError(form, config) {
  const message = config.messages?.network_error ?? 'We could not reach the server. Check your connection and try again.';
  setStatus(form, message, 'error');
  announce(config.selectors?.liveError ?? '#dt-form-errors', message);
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, unknown>} payload
 * @param {Record<string, unknown>} [submitted]
 */
function dispatchSuccess(form, payload, submitted) {
  try {
    const EventConstructor = globalThis.CustomEvent ?? globalThis.window?.CustomEvent;

    if (!EventConstructor || !globalThis.document?.dispatchEvent) {
      return;
    }

    globalThis.document.dispatchEvent(
      new EventConstructor('dt:form:success', {
        bubbles: true,
        detail: {
          form: form.dataset.dtRestForm ?? '',
          payload,
          email: typeof submitted?.email === 'string' ? submitted.email : '',
        },
      })
    );
  } catch {
    // Analytics hooks must not affect submission feedback.
  }
}

const STATUS_DISMISS_MS = 2 * 60 * 1000;

/** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
const statusDismissTimers = new WeakMap();

/**
 * @param {HTMLElement} status
 */
function clearStatusDismissTimer(status) {
  const timer = statusDismissTimers.get(status);

  if (timer === undefined) {
    return;
  }

  clearTimeout(timer);
  statusDismissTimers.delete(status);
}

/**
 * @param {HTMLElement} status
 */
function hideStatus(status) {
  clearStatusDismissTimer(status);
  status.setAttribute('hidden', 'hidden');
  status.textContent = '';
  status.classList.remove('dt-form__status--success', 'dt-form__status--error');
}

/**
 * @param {HTMLFormElement} form
 */
export function clearFormState(form) {
  const status = form.querySelector('.dt-form__status');

  if (isElement(status)) {
    hideStatus(status);
  }

  for (const field of form.querySelectorAll('.dt-form__field')) {
    if (isElement(field)) {
      field.classList.remove('dt-form__field--invalid');
    }
  }

  for (const control of form.querySelectorAll('input, select, textarea')) {
    if (isFieldControl(control)) {
      control.removeAttribute('aria-invalid');
      control.removeAttribute('aria-describedby');
    }
  }

  for (const error of form.querySelectorAll('.dt-form__field-error')) {
    if (isElement(error)) {
      error.setAttribute('hidden', 'hidden');
      error.textContent = '';
    }
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 * @param {'success' | 'error'} type
 */
function setStatus(form, message, type) {
  const status = form.querySelector('.dt-form__status');

  if (!isElement(status)) {
    return;
  }

  clearStatusDismissTimer(status);
  status.removeAttribute('hidden');
  status.textContent = stripHtmlMessage(message);
  status.classList.toggle('dt-form__status--success', type === 'success');
  status.classList.toggle('dt-form__status--error', type === 'error');

  const dismiss = status.ownerDocument.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'dt-form__status__dismiss';
  dismiss.setAttribute('aria-label', 'Cancel');
  dismiss.addEventListener('click', () => {
    hideStatus(status);
  });
  status.append(dismiss);

  statusDismissTimers.set(status, setTimeout(() => {
    hideStatus(status);
  }, STATUS_DISMISS_MS));

  if (typeof status.scrollIntoView === 'function') {
    status.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function stripHtmlMessage(value) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();

  if (trimmed === '' || (!trimmed.includes('<') && !trimmed.includes('&'))) {
    return trimmed;
  }

  return trimmed
    .replace(/<\/(p|div|li|h[1-6])\s*>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {HTMLFormElement} form
 * @param {string} fieldName
 * @param {string} message
 */
function applyFieldError(form, fieldName, message) {
  const control = form.querySelector(`[name="${fieldName}"]`);

  if (!isFieldControl(control)) {
    return;
  }

  const field = control.closest('.dt-form__field');

  if (isElement(field)) {
    field.classList.add('dt-form__field--invalid');
  }

  control.setAttribute('aria-invalid', 'true');
  const errorId = `${control.id || fieldName}-error`;
  const error = form.querySelector(`#${cssEscape(errorId)}`);

  if (isElement(error)) {
    error.removeAttribute('hidden');
    error.textContent = message;
    control.setAttribute('aria-describedby', errorId);
  }
}

/**
 * @param {string} selector
 * @param {string} message
 */
function announce(selector, message) {
  const region = globalThis.document?.querySelector(selector);

  if (isElement(region)) {
    region.textContent = message;
  }
}

/**
 * @param {unknown} value
 * @returns {value is HTMLFormElement}
 */
function isFormElement(value) {
  return isElement(value) && value.tagName === 'FORM';
}

/**
 * @param {HTMLFormElement} form
 */
function isSubscribeForm(form) {
  return form.classList.contains('dt-subscribe-form')
    || form.dataset.dtRestForm === 'subscribe';
}

/**
 * @param {HTMLFormElement} form
 * @param {FormConfig} config
 * @returns {boolean}
 */
function hasSubscribeProductSelection(form, config) {
  const selections = readSubscribeProductSelections(form);

  if (selections === null) {
    return true;
  }

  if (selections.some((item) => item.subscribed === true)) {
    return true;
  }

  const message = config.messages?.subscribe_products_required
    ?? 'Choose at least one publication to subscribe to.';
  const fieldset = form.querySelector('[data-dt-subscribe-products]');
  const error = form.querySelector('#dt-subscribe-products-error');

  if (isElement(fieldset)) {
    fieldset.classList.add('dt-form__field--invalid');
  }

  if (isElement(error)) {
    error.removeAttribute('hidden');
    error.textContent = message;
  }

  setStatus(form, message, 'error');
  return false;
}

/**
 * @param {unknown} value
 * @returns {value is HTMLElement}
 */
function isElement(value) {
  return typeof value === 'object' && value !== null && 'tagName' in value && 'classList' in value;
}

/**
 * @param {unknown} value
 * @returns {value is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement}
 */
function isFieldControl(value) {
  if (!isElement(value)) {
    return false;
  }

  const tag = value.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
}

/**
 * @param {string} value
 */
function cssEscape(value) {
  if (typeof globalThis.CSS !== 'undefined' && typeof globalThis.CSS.escape === 'function') {
    return globalThis.CSS.escape(value);
  }

  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
